/** The Executive Insights add-on's API types (docs/plans/executive-insights-plan.md section 5): what its endpoints answer. */

export const HEADLINE_KEYS = ['score', 'goldPct', 'callOutsPer100', 'fillPct', 'hppdWorked', 'retentionPct', 'headcount'] as const
export type HeadlineKey = (typeof HEADLINE_KEYS)[number]

export type Headlines = Record<HeadlineKey, number | null>

export interface PortfolioRow {
  facilityId: string
  name: string
  latest: string | null
  /** Each figure from the latest month that has it: a running month's fill and call-outs, the last closed month's score and Gold share. */
  figures: Headlines
  /** The month each figure is as of; absent from an API that predates it. */
  asOf?: Record<HeadlineKey, string | null>
  movement: Headlines
  series: Record<HeadlineKey, (number | null)[]>
}

export interface InsightsPortfolio {
  periods: string[]
  facilities: PortfolioRow[]
  totals: Omit<PortfolioRow, 'facilityId' | 'name'>
}

export interface ScoreTally {
  count: number
  sum: number
  min: number | null
  max: number | null
  bins: number[]
}

export interface BehaviorTally {
  count: number
  sum: number
  weightSum: number
}

/** One facility's month as the API keeps it; the screens read the parts they draw. */
export interface FacilityMonthRollup {
  facilityId: string
  period: string
  timezone: string
  computedAt: string
  rollupVersion: number
  closed: boolean
  released: boolean
  scores: ScoreTally
  tiers: Record<'gold' | 'silver' | 'bronze' | 'probationary', number>
  tierChanges: { up: number; down: number; held: number; newHire: number; provisionalHeld: number; noScore: number }
  behaviors: Record<string, BehaviorTally>
  outcomes: {
    scheduled: number
    completed: number
    callOuts: number
    noShows: number
    late: number
    chartsDue: number
    chartsCompleted: number
    byWeekday: number[]
    callOutsByWeekday: number[]
    byShiftType: Record<string, number>
    callOutsByShiftType: Record<string, number>
    callOutsByWeekdayShift: number[][]
  }
  coverage: {
    seatsRequired: number | null
    seatsPublished: number
    filledAtRelease: number
    filledPct: number | null
    minFilledPct: number | null
    fills: { roster: number; offer: number; firstCome: number; assigned: number; censusAdd: number; bridge: number }
    ranShort: number
    endedUnfilled: number
    withdrawn: number
    open: number
    swapsCompleted: number
    swapsDenied: number
    swapsReversed: number
    vacated: number
    daysOffTaken: number
    flexedOff: number
    floated: number
    recalled: number
    overtimeFlags: number
    daysInARowFlags: number
  }
  census: { daysRecorded: number; ppd: number; plannedDays: number; plannedPpd: number | null; byUnit: Record<string, { ppd: number; plannedPpd: number | null }> }
  staffing: { hoursScheduled: number; hoursWorked: number; hoursAgency: number; hoursFlexed: number; hoursOvertime: number; hoursTarget: number | null; byRole: Record<string, { scheduled: number; worked: number; target: number | null }> }
  workforce: { headcount: Record<'full_time' | 'part_time' | 'prn' | 'agency', number>; hired: number; left: number; fteNeeded: number; fteWithBuffer: number; fteOnRoster: number }
}

export interface InsightsMarker {
  at: string
  facilityId: string | null
  area: 'facility_config' | 'behaviors' | 'add_on_license' | 'add_on_switch'
  action: 'created' | 'updated' | 'deleted'
  subject: string | null
  fields: string[]
}

export interface InsightsQuarter {
  from: string
  to: string
  bridge: { opened: number; answered: number; filled: number; expired: number; cancelled: number; medianMinutesToFill: number | null }
  delivery: { byChannel: Record<string, { sent: number; failed: number; addressGone: number; segments: number }> } | null
}

export const SPAN_CHOICES = [3, 6, 12, 24] as const
export type SpanMonths = (typeof SPAN_CHOICES)[number]
